import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Drawer,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/Search'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { CleoConversation } from '../../../../models/cleo.model'
import { listConversations, deleteConversation } from '../../../../services/cleo.service'
import { handlePromise } from '../../../../utils/handlePromisse'

type ConversationSidebarProps = {
    activeId: number | null
    onSelect: (id: number) => void
    onNew: () => void
    refreshTrigger: number
    mobileOpen: boolean
    onMobileClose: () => void
}

const SIDEBAR_WIDTH = 300

function groupByDate(conversations: CleoConversation[]): { label: string; items: CleoConversation[] }[] {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today.getTime() - 86400000)
    const weekAgo = new Date(today.getTime() - 7 * 86400000)

    const groups: Record<string, CleoConversation[]> = {}

    for (const conv of conversations) {
        const date = new Date(conv.updatedAt || conv.createdAt || Date.now())
        let label: string

        if (date >= today) {
            label = 'Hoje'
        } else if (date >= yesterday) {
            label = 'Ontem'
        } else if (date >= weekAgo) {
            label = 'Últimos 7 dias'
        } else {
            label = 'Anteriores'
        }

        if (!groups[label]) groups[label] = []
        groups[label].push(conv)
    }

    const order = ['Hoje', 'Ontem', 'Últimos 7 dias', 'Anteriores']
    return order
        .filter(label => groups[label]?.length > 0)
        .map(label => ({ label, items: groups[label] }))
}

const ConversationSidebar: FC<ConversationSidebarProps> = ({
    activeId,
    onSelect,
    onNew,
    refreshTrigger,
    mobileOpen,
    onMobileClose,
}) => {
    const theme = useTheme()
    const [conversations, setConversations] = useState<CleoConversation[]>([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [search, setSearch] = useState('')

    const fetchConversations = useCallback(async (pageNum: number, append: boolean = false) => {
        const [result, error] = await handlePromise(listConversations(pageNum, 20))
        if (error || !result) return

        const data = result.data
        if (append) {
            setConversations(prev => [...prev, ...data.data])
        } else {
            setConversations(data.data)
        }
        setHasMore(data.pagination.currentPage < data.pagination.lastPage)
    }, [])

    useEffect(() => {
        setPage(1)
        fetchConversations(1)
    }, [refreshTrigger, fetchConversations])

    const handleLoadMore = () => {
        const next = page + 1
        setPage(next)
        fetchConversations(next, true)
    }

    const handleDelete = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation()
        const [, error] = await handlePromise(deleteConversation(id))
        if (!error) {
            setConversations(prev => prev.filter(c => c.id !== id))
            if (activeId === id) onNew()
        }
    }

    const filteredConversations = useMemo(() => {
        if (!search.trim()) return conversations
        const q = search.toLowerCase()
        return conversations.filter(c => c.title?.toLowerCase().includes(q))
    }, [conversations, search])

    const grouped = useMemo(() => groupByDate(filteredConversations), [filteredConversations])

    const content = (
        <Box
            sx={{
                width: SIDEBAR_WIDTH,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                borderRight: 1,
                borderColor: 'divider',
            }}
        >
            <Box sx={{ p: 2, pb: 1.5 }}>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { onNew(); onMobileClose() }}
                    sx={{
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                        mb: 1.5,
                    }}
                >
                    Nova conversa
                </Button>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar conversas..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            fontSize: '0.85rem',
                        },
                    }}
                />
            </Box>

            <List sx={{ flex: 1, overflow: 'auto', px: 1, pt: 0.5 }}>
                {grouped.map((group) => (
                    <Box key={group.label}>
                        <Typography
                            variant="overline"
                            sx={{
                                display: 'block',
                                px: 1.5,
                                pt: 1.5,
                                pb: 0.5,
                                color: 'text.secondary',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                letterSpacing: 1,
                            }}
                        >
                            {group.label}
                        </Typography>
                        {group.items.map((conv) => (
                            <ListItemButton
                                key={conv.id}
                                selected={conv.id === activeId}
                                onClick={() => { onSelect(conv.id); onMobileClose() }}
                                sx={{
                                    borderRadius: 2,
                                    mb: 0.25,
                                    pr: 1,
                                    py: 0.75,
                                    transition: 'all 0.15s ease',
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.lighter',
                                        borderLeft: '3px solid',
                                        borderColor: 'primary.main',
                                    },
                                    '&:hover .delete-btn': { opacity: 0.7 },
                                }}
                            >
                                <ChatBubbleOutlineIcon
                                    sx={{
                                        fontSize: 16,
                                        color: conv.id === activeId ? 'primary.main' : 'grey.400',
                                        mr: 1,
                                        flexShrink: 0,
                                    }}
                                />
                                <ListItemText
                                    primary={conv.title || 'Nova conversa'}
                                    primaryTypographyProps={{
                                        noWrap: true,
                                        variant: 'body2',
                                        fontSize: '0.85rem',
                                        fontWeight: conv.id === activeId ? 600 : 400,
                                    }}
                                />
                                <Tooltip title="Excluir conversa" placement="top">
                                    <IconButton
                                        className="delete-btn"
                                        size="small"
                                        aria-label="Excluir conversa"
                                        onClick={(e) => handleDelete(e, conv.id)}
                                        sx={{
                                            opacity: 0,
                                            transition: 'opacity 0.2s ease',
                                            '&:hover': { opacity: 1, color: 'error.main' },
                                        }}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </ListItemButton>
                        ))}
                    </Box>
                ))}
                {filteredConversations.length === 0 && (
                    <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            {search ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
                        </Typography>
                    </Box>
                )}
            </List>
            {hasMore && !search && (
                <Box sx={{ p: 1 }}>
                    <Button fullWidth size="small" onClick={handleLoadMore} sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Carregar mais
                    </Button>
                </Box>
            )}
        </Box>
    )

    return (
        <>
            <Box sx={{ display: { xs: 'none', md: 'block' }, height: '100%' }}>
                {content}
            </Box>
            <Drawer
                open={mobileOpen}
                onClose={onMobileClose}
                sx={{ display: { xs: 'block', md: 'none' } }}
                PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
            >
                {content}
            </Drawer>
        </>
    )
}

export default ConversationSidebar